import React, { useEffect, useState } from "react";
import { Navbar, Nav, Container } from "react-bootstrap";
import NavDropDown from "react-bootstrap/NavDropdown";
import useToken from "../authentication/token";
import { Toaster } from "react-hot-toast";

export default function NavigationBar() {
  const { firstName, lastName, role, token, verifyToken, removeToken } = useToken();
  const [activeLink, setActiveLink] = useState("");

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token, verifyToken]);

  // Set active link based on current path
  useEffect(() => {
    setActiveLink(window.location.pathname);
  }, []);

  const handleNavClick = (path, clearStorage = null) => {
    setActiveLink(path);
    if (clearStorage) {
      localStorage.removeItem(clearStorage);
    }
  };

  const handleLogout = () => {
    removeToken();
    setActiveLink("/");
  };

  const isActive = (path) => activeLink === path;

  return (
    <>
      <Navbar
        sticky="top"
        className="main-color shadow-sm"
        expand="lg"
        variant="dark"
      >
        <Container fluid style={{ maxWidth: "98%", margin: "0 auto" }}>
          {/* Brand */}
          <Navbar.Brand
            href="/"
            className="fw-bold fs-4"
            onClick={() => handleNavClick("/")}
          >
            Chemistry Commons
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            className="border-0"
          />

          <Navbar.Collapse id="basic-navbar-nav">
            {/* Main Navigation Links */}
            <Nav className="me-auto">
              <Nav.Link
                href="/reactions"
                className={`nav-link-custom ${isActive("/reactions") ? "active" : ""}`}
                onClick={() => handleNavClick("/reactions", "rxnData")}
              >
                Reactions
              </Nav.Link>

              <Nav.Link
                href="/synthons"
                className={`nav-link-custom ${isActive("/synthons") ? "active" : ""}`}
                onClick={() => handleNavClick("/synthons", "synData")}
              >
                Synthons
              </Nav.Link>

              <Nav.Link
                href="/tutorials"
                className={`nav-link-custom ${isActive("/tutorials") ? "active" : ""}`}
                onClick={() => handleNavClick("/tutorials")}
              >
                Tutorials
              </Nav.Link>

              <Nav.Link
                href="/downloads"
                className={`nav-link-custom ${isActive("/downloads") ? "active" : ""}`}
                onClick={() => handleNavClick("/downloads")}
              >
                Downloads
              </Nav.Link>

              <Nav.Link
                href="/ab3"
                className={`nav-link-custom ${isActive("/ab3") ? "active" : ""}`}
                onClick={() => handleNavClick("/ab3")}
              >
                AB3
              </Nav.Link>

              {/* Bioisosteres Dropdown */}
              <NavDropDown
                title="Bioisosteres"
                id="bioisosteres-dropdown"
                className="dropdown-custom"
              >
                <NavDropDown.Item
                  href="/bioisosteres/transform"
                  onClick={() => handleNavClick("/bioisosteres/transform")}
                >
                  <i className="me-2">🔄</i>
                  Transform
                </NavDropDown.Item>
                <NavDropDown.Item
                  href="/bioisosteres/browse"
                  onClick={() => handleNavClick("/bioisosteres/browse")}
                >
                  <i className="me-2">📖</i>
                  Browse
                </NavDropDown.Item>
              </NavDropDown>

              <Nav.Link
                href="https://swcc.docking.org/search.html"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link-custom external-link"
              >
                Search <i className="ms-1">🔗</i>
              </Nav.Link>
            </Nav>

            {/* User Authentication Section */}
            <Nav className="ms-auto">
              {token ? (
                <NavDropDown
                  title={
                    <span className="d-flex align-items-center">
                      <div className="user-avatar me-2">
                        {firstName?.charAt(0)}{lastName?.charAt(0)}
                      </div>
                      <span className="d-none d-md-inline">
                        {firstName} {lastName}
                      </span>
                    </span>
                  }
                  id="user-dropdown"
                  className="user-dropdown"
                  align="end"
                >
                  {role === "admin" && (
                    <>
                      <NavDropDown.Item
                        href="/admin"
                        onClick={() => handleNavClick("/admin")}
                      >
                        <i className="me-2">⚙️</i>
                        Admin Menu
                      </NavDropDown.Item>
                      <NavDropDown.Divider />
                    </>
                  )}
                  <NavDropDown.Header>My Reactions</NavDropDown.Header>

                  <NavDropDown.Item
                    href="/user/myReactions"
                    onClick={() => handleNavClick("/user/myReactions")}
                    className="ps-4"
                  >
                    <i className="me-2">⏳</i>
                    In Review
                  </NavDropDown.Item>

                  <NavDropDown.Item
                    href="/user/reactions/approved"
                    onClick={() => handleNavClick("/user/reactions/approved")}
                    className="ps-4"
                  >
                    <i className="me-2">✅</i>
                    Approved Reactions
                  </NavDropDown.Item>

                  <NavDropDown.Divider />

                  <NavDropDown.Item onClick={handleLogout} className="text-danger">
                    <i className="me-2">🚪</i>
                    Logout
                  </NavDropDown.Item>
                </NavDropDown>
              ) : (
                <NavDropDown
                  title={
                    <span>
                      <i className="me-2">👤</i>
                      Account
                    </span>
                  }
                  id="auth-dropdown"
                  className="auth-dropdown"
                  align="end"
                >
                  <NavDropDown.Item
                    href="/login"
                    onClick={() => handleNavClick("/login")}
                  >
                    <i className="me-2">🔐</i>
                    Login
                  </NavDropDown.Item>

                  <NavDropDown.Item
                    href="/register"
                    onClick={() => handleNavClick("/register")}
                  >
                    <i className="me-2">📝</i>
                    Register
                  </NavDropDown.Item>
                </NavDropDown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Add custom styles */}
      <style jsx>{`
        .nav-link-custom {
          position: relative;
          transition: all 0.3s ease;
          border-radius: 6px;
          margin: 0 2px;
        }
        
        .nav-link-custom:hover {
          background-color: rgba(255, 255, 255, 0.1);
          transform: translateY(-1px);
        }
        
        .nav-link-custom.active {
          background-color: rgba(255, 255, 255, 0.15);
          font-weight: 500;
        }
        
        .external-link {
          opacity: 0.9;
        }
        
        .user-avatar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }
        
        .dropdown-custom .dropdown-menu,
        .user-dropdown .dropdown-menu,
        .auth-dropdown .dropdown-menu {
          border: none;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border-radius: 8px;
          padding: 8px 0;
          margin-top: 8px;
        }
        
        .dropdown-item {
          padding: 8px 16px;
          transition: all 0.2s ease;
          border-radius: 4px;
          margin: 2px 8px;
        }
        
        .dropdown-item:hover {
          background-color: #f8f9fa;
          transform: translateX(4px);
        }
        
        .navbar-toggler {
          padding: 6px 8px;
          border-radius: 6px;
        }
        
        .navbar-toggler:focus {
          box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
        }
        
        @media (max-width: 991.98px) {
          .nav-link-custom {
            margin: 4px 0;
            padding: 12px 16px;
          }
          
          .user-avatar {
            width: 28px;
            height: 28px;
            font-size: 11px;
          }
        }
      `}</style>

      <Toaster
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </>
  );
}